import { useState, useEffect, ChangeEvent } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { TextInput, TextInputProps } from '@orbita-ui/core';

interface FormattedAmountInputProps extends Omit<TextInputProps, 'value' | 'onChange' | 'onBlur' | 'name' | 'label'> {
  name: string;
  label: string;
  validationRules?: object;
}

/**
 * Input de monto con formato de miles, implementado como solución a las limitaciones
 * del componente `AmountInput` de `@orbita-ui/core`.
 *
 * ### Motivación
 * El componente `AmountInput` original de la librería no permitía el ingreso de
 * montos con una gran cantidad de dígitos, truncando el valor. Este componente
 * personalizado resuelve ese problema utilizando el `TextInput` estándar de Orbita
 * y añadiéndole una capa de formato manual.
 *
 * ### Futuro
 * Este componente podría ser deprecado y eliminado si una futura actualización de
 * `@orbita-ui/core` soluciona las limitaciones del `AmountInput` original.
 */
export const FormattedAmountInput = ({
                                       name,
                                       label,
                                       validationRules,
                                       ...rest
                                     }: FormattedAmountInputProps) => {
  const { control } = useFormContext();

  const {
    field: { onChange, onBlur, value },
    fieldState: { error },
  } = useController({
    name,
    control,
    rules: validationRules,
    defaultValue: '',
  });

  const [displayValue, setDisplayValue] = useState('');

  const formatNumber = (numStr: string): string => {
    if (!numStr) return '';
    const [integerPart, decimalPart] = numStr.split(',');
    const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return decimalPart !== undefined ? `${formattedIntegerPart},${decimalPart}` : formattedIntegerPart;
  };

  // Sincroniza el valor visible si el valor del formulario cambia.
  useEffect(() => {
    if (value === null || value === undefined) {
      setDisplayValue('');
      return;
    }
    // Convierte el número a un string con ',' como separador decimal para mostrarlo
    const initialValue = String(value).replace('.', ',');
    setDisplayValue(formatNumber(initialValue));
  }, [value]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;

    const valueWithoutThousands = rawValue.replace(/\./g, '');

    let sanitizedValue = valueWithoutThousands.replace(/[^\d,]/g, '');
    const firstCommaIndex = sanitizedValue.indexOf(',');
    if (firstCommaIndex !== -1) {
      sanitizedValue =
        sanitizedValue.substring(0, firstCommaIndex + 1) +
        sanitizedValue.substring(firstCommaIndex + 1).replace(/,/g, '');
    }

    const parts = sanitizedValue.split(',');
    if (parts[1] && parts[1].length > 2) {
      parts[1] = parts[1].substring(0, 2);
      sanitizedValue = parts.join(',');
    }

    const numericStringForParsing = sanitizedValue.replace(',', '.');
    onChange(numericStringForParsing ? parseFloat(numericStringForParsing) : null);

    setDisplayValue(formatNumber(sanitizedValue));
  };

  return (
    <TextInput
      label={label}
      value={displayValue}
      onChange={handleChange}
      onBlur={onBlur}
      fullWidth
      errorText={error?.message}
      invalid={!!error}
      {...rest}
    />
  );
};